import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Password strength validation
const validatePassword = (password: string): { isValid: boolean; message: string } => {
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (password.length < minLength) {
    return { isValid: false, message: 'Password must be at least 8 characters long.' };
  }
  if (!hasUppercase) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter.' };
  }
  if (!hasLowercase) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter.' };
  }
  if (!hasNumber) {
    return { isValid: false, message: 'Password must contain at least one number.' };
  }
  if (!hasSymbol) {
    return { isValid: false, message: 'Password must contain at least one special character.' };
  }

  return { isValid: true, message: '' };
};

export const useAuthOperations = () => {
  const { toast } = useToast();

  const signUp = useCallback(async (email: string, password: string, userData?: any) => {
    try {
      console.log('Attempting signup...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for signup - matches official documentation
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) {
        console.error('Signup error:', error);
        return { error: { message: error.message || 'Signup failed. Please try again.' } };
      }
      
      console.log('Signup successful');
      return { data, error: null };
    } catch (err) {
      console.error('Signup exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during signup. Please try again.' 
        } 
      };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      console.log('Attempting signin...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for signin - matches official documentation
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('Signin error:', error);
        return { error: { message: error.message || 'Signin failed. Please try again.' } };
      }
      
      console.log('Signin successful');
      return { data, error: null };
    } catch (err) {
      console.error('Signin exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during signin. Please try again.' 
        } 
      };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      console.log('Attempting signout...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return;
      }
      
      // Use Supabase client for signout - matches official documentation
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Signout error:', error);
        toast({
          title: "Sign Out Error",
          description: error.message || "Failed to sign out. Please try again.",
          variant: "destructive"
        });
      } else {
        console.log('Signout successful');
        toast({
          title: "Signed Out",
          description: "You have been successfully signed out.",
        });
      }
    } catch (err) {
      console.error('Signout exception:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred during sign out. Please try again.",
        variant: "destructive"
      });
    }
  }, [toast]);

  const enableMFA = useCallback(async () => {
    try {
      console.log('Attempting MFA enrollment...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for MFA enrollment - matches official documentation
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      });

      if (error) {
        console.error('MFA enrollment error:', error);
        return { error: { message: error.message || 'MFA enrollment failed. Please try again.' } };
      }
      
      console.log('MFA enrollment successful');
      return { data, error: null };
    } catch (err) {
      console.error('MFA enrollment exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during MFA enrollment. Please try again.' 
        } 
      };
    }
  }, []);

  const verifyMFA = useCallback(async (factorId: string, challengeId: string, code: string) => {
    try {
      console.log('Attempting MFA verification...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for MFA verification - matches official documentation
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code
      });

      if (error) {
        console.error('MFA verification error:', error);
        return { error: { message: error.message || 'MFA verification failed. Please try again.' } };
      }
      
      console.log('MFA verification successful');
      return { data, error: null };
    } catch (err) {
      console.error('MFA verification exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during MFA verification. Please try again.' 
        } 
      };
    }
  }, []);

  // ARCHIVED: Google sign-in functions temporarily disabled
  /*
  const signInWithGoogle = useCallback(async () => {
    try {
      console.log('Attempting Google OAuth signin...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for Google OAuth - matches official documentation
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });

      if (error) {
        console.error('Google OAuth error:', error);
        return { error: { message: error.message || 'Google sign-in failed. Please try again.' } };
      }
      
      console.log('Google OAuth initiated successfully');
      return { error: null };
    } catch (err) {
      console.error('Google OAuth exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during Google sign-in. Please try again.' 
        } 
      };
    }
  }, []);

  const signInWithIdToken = useCallback(async (params: { provider: string; token: string; nonce?: string }) => {
    try {
      console.log('Attempting Google ID token signin...');
      
      // Check network connectivity first
      if (!navigator.onLine) {
        console.error('No internet connection');
        return { error: { message: 'No internet connection. Please check your network and try again.' } };
      }
      
      // Use Supabase client for Google ID token signin - matches official documentation
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: params.provider,
        token: params.token,
        nonce: params.nonce,
      });

      if (error) {
        console.error('Google ID token signin error:', error);
        return { error: { message: error.message || 'Google sign-in failed. Please try again.' } };
      }
      
      console.log('Google ID token signin successful');
      return { data, error: null };
    } catch (err) {
      console.error('Google ID token signin exception:', err);
      
      // Handle specific network errors
      if (err instanceof TypeError && err.message.includes('fetch')) {
        return { 
          error: { 
            message: 'Network error: Unable to connect to authentication service. Please check your internet connection and try again.' 
          } 
        };
      }
      
      return { 
        error: { 
          message: 'An unexpected error occurred during Google sign-in. Please try again.' 
        } 
      };
    }
  }, []);
  */

  // Placeholder functions for archived Google sign-in
  const signInWithGoogle = useCallback(async () => {
    console.log('Google sign-in is currently archived');
    return { error: { message: 'Google sign-in is temporarily unavailable.' } };
  }, []);

  const signInWithIdToken = useCallback(async (params: { provider: string; token: string; nonce?: string }) => {
    console.log('Google ID token sign-in is currently archived');
    return { error: { message: 'Google sign-in is temporarily unavailable.' } };
  }, []);

  return {
    signUp,
    signIn,
    signOut,
    enableMFA,
    verifyMFA,
    signInWithGoogle,
    signInWithIdToken
  };
};
